import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import { t } from 'i18next';
import { FC, useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { useConfig, useResponsiveValue } from '#/hooks';
import { MenuConfig } from '#/service/config';
import {
  BreakpointKeys,
  MenuBackgroundStyle,
  ScrollBehaviour,
} from '#/service/constants';
import { MenuType } from '#/service/constants/menu';
import { Path } from '#/service/routes';

import Banner from '../Banner/Banner';
import HamburgerMenu from './HamburgerMenu';
import { Menu } from './Menu';
import { useCachedMenu } from './useCachedMenu';
import useOfflineNetwork from './useOfflineNetwork';
import { getHardcodedLoginMenuItem, MenuProps } from './utils';

export enum HeaderMode {
  /** Logo and all the configured menu items */
  Default = 'DEFAULT',
  /** Logo and login & registration link items. Mainly for the unauthenticated home page. */
  Reduced = 'REDUCED',
  /** Only logo. For modals and flows. */
  Empty = 'EMPTY',
}

interface HeaderDivProps {
  /** The menu data */
  menu: MenuConfig;
  /** Set the menu collapsed, regardless of scroll position */
  alwaysCollapsed?: boolean;
  /** Prefix for the menuitem caching */
  cacheKey?: string;
  /** What contents should be rendered. */
  mode?: HeaderMode;
}

const HeaderContainer = styled.header`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  z-index: 3;

  /*
  Menu components and gradients are nested in tricky ways. In some cases, gradients can cover clickable elements.
  Making this container and its non-clickable children in both top & burger menus ignore pointer events to prevent this issue.
  */
  pointer-events: none;

  & a,
  & button,
  & img {
    pointer-events: auto;
  }
`;

const Header: FC<HeaderDivProps> = ({
  menu,
  cacheKey,
  alwaysCollapsed = false,
  mode = HeaderMode.Default,
}) => {
  const {
    theme: {
      menu: { background },
    },
    menusConfig: {
      style: { scrollBehaviour },
    },
  } = useConfig();
  const { Component } = useResponsiveValue<ResponsiveProps>(breakpointMap);
  const { isBannerHidden } = useOfflineNetwork();
  const cachedMenu = useCachedMenu(menu, cacheKey);
  const { pathname } = useLocation();

  // are we on an "always collapsed menu" page?
  const isAlwaysCollapsed = [Path.Registration, Path.Settings].some((path) =>
    matchPath(path, pathname)
  );

  // "always collapsed menu" is always opaque
  const menuBackgroundStyle = isAlwaysCollapsed
    ? MenuBackgroundStyle.Opaque
    : background;

  const [isCollapsed, setIsCollapsed] = useState(
    menuBackgroundStyle === MenuBackgroundStyle.Opaque
  );

  const [isHidden, setIsHidden] = useState(false);

  useScrollPosition(
    ({ prevPos, currPos }) => {
      const isScrolledDown = currPos.y < 0;
      const isScrollingDown = prevPos.y > currPos.y;
      const isOverThreshold = Math.abs(prevPos.y - currPos.y) > 1;

      // collapse gradient menu into opaque when being scrolled down
      if (
        menuBackgroundStyle === MenuBackgroundStyle.Gradient &&
        isCollapsed !== isScrolledDown
      ) {
        setIsCollapsed(isScrolledDown);
      }

      // in reduced mode, scroll behaviour is always hidden, regardless of the config
      const isScrollBehaviourHidden =
        scrollBehaviour === ScrollBehaviour.Hidden ||
        mode === HeaderMode.Reduced;

      // hide menu when scrolling downwards, show it when scrolling upwards
      // except on "always collapsed menu" pages
      if (
        isScrollBehaviourHidden &&
        !isAlwaysCollapsed &&
        isOverThreshold &&
        isHidden !== isScrollingDown
      ) {
        setIsHidden(isScrollingDown);
      }
    },
    [
      isAlwaysCollapsed,
      isCollapsed,
      isHidden,
      menuBackgroundStyle,
      scrollBehaviour,
    ]
  );

  useEffect(() => {
    setIsCollapsed(menuBackgroundStyle === MenuBackgroundStyle.Opaque);
  }, [menuBackgroundStyle]);

  const menuProps = useMemo(
    () => ({
      ...cachedMenu,
      ...(mode === HeaderMode.Reduced && {
        mainMenuItems: [],
        linkMenuItems: [getHardcodedLoginMenuItem()],
      }),
      ...(mode === HeaderMode.Empty && {
        mainMenuItems: [],
        linkMenuItems: [],
      }),
      isCollapsed: alwaysCollapsed || isCollapsed,
      isHidden,
    }),
    [alwaysCollapsed, cachedMenu, isCollapsed, isHidden, mode]
  );

  return (
    <HeaderContainer data-testid="header">
      <Banner
        error={false}
        hidden={isBannerHidden}
        message={t('error.networkConnection.message')}
      />
      {menu.type === MenuType.HAMBURGER ? (
        <HamburgerMenu {...menuProps} />
      ) : (
        <Component {...menuProps} />
      )}
    </HeaderContainer>
  );
};

export default Header;

interface ResponsiveProps {
  Component: FC<MenuProps>;
}

const breakpointMap = {
  [BreakpointKeys.XS]: { Component: HamburgerMenu },
  [BreakpointKeys.SM]: { Component: HamburgerMenu },
  [BreakpointKeys.MD]: { Component: HamburgerMenu },
  [BreakpointKeys.LG]: { Component: HamburgerMenu },
  [BreakpointKeys.XL]: { Component: Menu },
  [BreakpointKeys.XXL]: { Component: Menu },
};
